'use client';

import React, { useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import Button from '@/components/button/button';

export default function PersonalDataRequestForm() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    altEmail: '',
    removeData: false,
    requestData: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<string | null>(null);
  const [errorResult, setErrorResult] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);
    setErrorResult(null);

    // Compose Zendesk JSON
    let description = `Hello, the following person has filled out the Request for Personal Data Retrieval or Removal\n\nName: ${form.firstName} ${form.lastName}\nEmail: ${form.email}`;
    if (form.altEmail) {
      description += `\nAlternate Email(s): ${form.altEmail}`;
    }
    if (form.removeData) {
      description += `\nThis user would like their personal data removed`;
    }
    if (form.requestData) {
      description += `\nThis user would like a copy of their personal data`;
    }
    const payload = {
      ticket: {
        requester: {
          local_id: 1,
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
        },
        priority: 'high',
        tags: ['personal_data_request'],
        subject: 'Request for Personal Data Retrieval or Removal',
        description,
      },
    };

    try {
      const res = await fetch('/api/zendesk-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setSubmitResult('Request submitted successfully!');
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        altEmail: '',
        removeData: false,
        requestData: false,
      });
    } catch {
      setErrorResult('There was an error submitting the request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>First Name</label>
            <input
              type="text"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Last Name</label>
            <input
              type="text"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Alternate Email(s)</label>
            <input
              type="text"
              name="altEmail"
              value={form.altEmail}
              onChange={handleChange}
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>I Would Like To:</label>
            <div className="form-option">
              <label>
                <input
                  type="checkbox"
                  name="removeData"
                  checked={form.removeData}
                  onChange={handleChange}
                />
                Remove My Personal Data
              </label>
            </div>
            <div className="form-option">
              <label>
                <input
                  type="checkbox"
                  name="requestData"
                  checked={form.requestData}
                  onChange={handleChange}
                />
                Request My Personal Data
              </label>
            </div>
          </div>
        </Col>
        <div className="btn-wrap">
          <Button type="submit" variant="secondary" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Send Form'}
          </Button>
        </div>
        {submitResult && (
          <div className="mt-3 alert alert-success">{submitResult}</div>
        )}
        {errorResult && (
          <div className="mt-3 alert alert-danger">{errorResult}</div>
        )}
      </Row>
    </Form>
  );
}
